import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchFromApi } from '../apiClient';
import {
  CreateProductPayload,
  Product,
  UploadProductAvatarResponse,
  CategoryResponse,
} from 'types/productsTypes';
import { PaginationQueryParams } from 'types/queryTypes';
import { SortOrder } from 'hooks/useTableControls';

// Base API URL
const REACT_APP_DARWIN_URL = import.meta.env.VITE_DARWIN_URL;

// Query keys
const PRODUCTS_QUERY_KEY = 'products';
const PRODUCT_QUERY_KEY = 'product';
const PRODUCT_BY_ID_QUERY_KEY = 'product_by_id';

/**
 * Fetch products tree
 */
export const useGetProductsTree = (
  companyId: string,
  withFinalProducts = true
) => {
  return useQuery({
    queryKey: [PRODUCTS_QUERY_KEY, companyId, withFinalProducts],
    queryFn: () =>
      fetchFromApi<CategoryResponse[]>(
        `${REACT_APP_DARWIN_URL}/products/tree/${companyId}?withFinalProducts=${withFinalProducts}`
      ),
  });
};
/**
 * Fetch products by company ID.
 * TODO: Verify this query and add appropriate types.
 */
export const useGetProductsByCompany = ({
  companyId,
  page = 1,
  limit = 10,
  query = '',
  sortOrder = { sortBy: 'id', order: 'ASC' },
}: PaginationQueryParams & { companyId: string } & {
  sortOrder: SortOrder;
}) => {
  return useQuery({
    queryKey: [PRODUCTS_QUERY_KEY, limit, page, query, sortOrder],
    queryFn: () =>
      fetchFromApi<Product[]>(
        `${REACT_APP_DARWIN_URL}/products/${companyId}?limit=${limit}&page=${page}&search=${query}&order=${sortOrder.order}&sortBy=${sortOrder.sortBy}`
      ),
    enabled: !!companyId,
    refetchOnWindowFocus: false,
  });
};

/**
 * Get product by id.
 */
export const useGetProductById = (companyId: string, productId: string) => {
  return useQuery({
    queryKey: [PRODUCT_BY_ID_QUERY_KEY, companyId, productId],
    queryFn: () =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/products/${companyId}/${productId}`
      ),
    enabled: !!companyId && !!productId,
  });
};

/**
 * Create a new product.
 */
export const useCreateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      productData,
    }: {
      companyId: string;
      productData: CreateProductPayload;
    }) =>
      fetchFromApi(`${REACT_APP_DARWIN_URL}/products/${companyId}`, {
        method: 'POST',
        body: JSON.stringify(productData),
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PRODUCTS_QUERY_KEY] });
    },
  });
};

/**
 * Upload product image (avatar)
 */
export const useUploadProductAvatar = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      file,
      folder = 'products',
    }: {
      file: File;
      folder?: string;
    }) => {
      const formData = new FormData();
      formData.append('file', file);

      return fetchFromApi<UploadProductAvatarResponse>(
        `${REACT_APP_DARWIN_URL}/files/upload/${folder}`,
        {
          method: 'POST',
          body: formData,
        }
      );
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PRODUCT_QUERY_KEY] });
    },
  });
};

/**
 * Activate or deactivate product
 */
export const useActivateDeactivateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      companyId,
      productId,
      payload,
    }: {
      companyId: string;
      productId: string | number;
      payload: { active: boolean };
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/products/${companyId}/${productId}`,
        {
          method: 'PUT',
          body: JSON.stringify(payload),
        }
      ),
    onSuccess: () => {
      // Invalidate the cache of the user list after activating/deactivating an user
      queryClient.invalidateQueries({ queryKey: [PRODUCTS_QUERY_KEY] });
    },
  });
};

/**
 * Edit a product.
 */
export const useEditProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productData,
      companyId,
      productId,
    }: {
      productData: CreateProductPayload;
      companyId: string | number;
      productId: string | number;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/products/${companyId}/${productId}`,
        {
          method: 'PUT',
          body: JSON.stringify(productData),
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PRODUCTS_QUERY_KEY] });
    },
  });
};

export const useDeleteProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      productId,
      companyId,
    }: {
      productId: string | number;
      companyId: string;
    }) =>
      fetchFromApi(
        `${REACT_APP_DARWIN_URL}/products/${companyId}/${productId}`,
        {
          method: 'DELETE',
        }
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [PRODUCTS_QUERY_KEY] });
    },
  });
};
